import React, { useState } from 'react'

import { ChevronDown, ChevronUp, Circle } from 'lucide-react'

import { cn } from 'utils/utils'

import { Button } from '@harvey-frontend/components/ui/button'
import { ScrollArea } from 'components/ui/scroll-area'

interface CollapsibleSummaryProps {
  title: React.ReactNode
  children: React.ReactNode
  color: string
  issueCount: number
  className?: string
  isCollapsed?: boolean
  onToggle?: () => void
}

export const CollapsibleSummary: React.FC<CollapsibleSummaryProps> = ({
  title,
  children,
  color,
  issueCount,
  className,
  isCollapsed = true,
  onToggle,
}) => {
  const [topBorderVisible, setTopBorderVisible] = useState(false)
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setTopBorderVisible(e.currentTarget.scrollTop > 0)
  }
  const dropdownIcon = (() => {
    if (!children) {
      return null
    }
    if (isCollapsed) {
      return <ChevronDown className="h-4 w-4 text-muted" />
    }
    return <ChevronUp className="h-4 w-4 text-muted" />
  })()
  return (
    <div className={cn('flex flex-col rounded-lg border', className)}>
      <Button
        onClick={() => {
          if (children) {
            onToggle?.()
          }
        }}
        className={cn(
          'flex h-fit w-full items-center justify-between gap-2 px-4 py-2 transition hover:bg-transparent',
          {
            'cursor-not-allowed': !children,
          }
        )}
        variant="ghost"
      >
        <div className="flex flex-1 items-center gap-2">
          <Circle className="h-2.5 w-2.5" fill={color} stroke={color} />
          <div className="flex flex-1 justify-between">
            <span className="text-sm font-medium leading-5 text-primary">
              {title}
            </span>
            <span className="text-xs leading-5 text-secondary">
              {issueCount} {issueCount === 1 ? 'rule' : 'rules'}
            </span>
          </div>
          {dropdownIcon}
        </div>
      </Button>
      {children && (
        <ScrollArea
          maxHeight="max-h-[50vh]"
          className={cn('transition-[border]', {
            'border-t': topBorderVisible,
            'pb-0': isCollapsed,
          })}
          onScroll={onScroll}
        >
          {!isCollapsed && <div className="px-2 py-1">{children}</div>}
        </ScrollArea>
      )}
    </div>
  )
}
